// components/BulletinDocuments.jsx
import React, { useMemo,useState } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetBulletinFileListQuery } from '../../../../api/endpoints/bulletinDocument';
import toastService from '../../../../utils/toastService';
import { getEnv } from '../../../../config/runtimeConfig';

export default function BulletinDocuments({ bulletinNo }) {
    const { th } = useLanguage();
    const [loadingIndex, setLoadingIndex] = useState(new Set());

    const bulletId = useMemo(() => {
        if (!bulletinNo) return null;
        const parsed = parseInt(bulletinNo, 10);
        return isNaN(parsed) ? bulletinNo.replace(/^0+/, '') || '0' : String(parsed);
    }, [bulletinNo]);

    const { data: bulletinFiles, isLoading, error } = useGetBulletinFileListQuery(bulletId, {
        skip: !bulletId
    });

    const fileList = useMemo(() => {
        if (!bulletinFiles) return [];
        if (Array.isArray(bulletinFiles)) return bulletinFiles;
        if (bulletinFiles?.data && Array.isArray(bulletinFiles.data)) return bulletinFiles.data;
        return [];
    }, [bulletinFiles]);

    const handleDownload = async (rowData, index) => {
        setLoadingIndex(prev => new Set([...prev, index]));
        try {
            const downloadUrl = rowData.downloadUrl;
            if (!downloadUrl) {
                toastService.error(th('serviceBulletinReport.documents.downloadError'));
                return;
            }

            const baseUrl = getEnv('REACT_APP_API_URL')?.replace(/\/$/, '') || '';
            const url = `${baseUrl}${downloadUrl}`;

            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/octet-stream'
                }
            });

            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || errorData.errorMessage || 'Download failed');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            if (blob.size === 0) {
                toastService.warn(th('serviceBulletinReport.documents.emptyFile'));
                return;
            }

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;

            const contentDisposition = response.headers.get('content-disposition');
            let fileName = rowData.fileName || 'download';
            if (contentDisposition) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            toastService.success(th('serviceBulletinReport.documents.downloadSuccess'));
        } catch (error) {
            console.error('Bulletin file download error:', error);
            toastService.error(th('serviceBulletinReport.documents.downloadError'));
        }
        finally {
            setLoadingIndex(prev => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-content-center align-items-center p-3">
                <ProgressSpinner style={{ width: '30px', height: '30px' }} />
            </div>
        );
    }

  if (error || !fileList || fileList.length === 0) {
    return (
        <div className="flex align-items-center gap-2 p-2 border-round text-sm" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
            <i className="pi pi-info-circle text-sm" style={{ color: '#F59E0B' }}></i>
            <span style={{ color: '#92400E' }}>{th('serviceBulletinReport.documents.noDocuments')}</span>
        </div>
    );
}
 return (
    <div className="flex flex-wrap justify-content-center gap-2">
        {fileList.map((file, index) => (
            <Button
                key={index}
                icon={loadingIndex.has(index) ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                className="p-button-primary"
                onClick={() => handleDownload(file, index)}
                disabled={loadingIndex.has(index)}
                tooltip={file.fileName || file.name || file}
                tooltipOptions={{ position: 'top' }}
                style={{ width: '40px', height: '40px' }}
            />
        ))}
    </div>
);
}